import { For, Show, createMemo, createSignal, type Component } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Text } from '~ui/text';
import { formatCurrency, formatDay, formatPercent } from '~/lib/format';
import { clearHover, hoverAt, setHoverAt } from '~/stores/crosshair-store';

// What the account has been worth, against what went into it.
//
// Two lines, one axis, in dollars. The gap between them is the gain — which is
// the only reason to draw the value line at all: on its own it cannot tell
// growth from a deposit, and both look like the line going up.
//
// Cost is a step function and is drawn as one: money arrives on the day it
// arrives, and joining deposits with a diagonal would draw a gradual saving
// habit nobody has.
const VB_W = 100;
const VB_H = 40;
const PAD = 2;
const PAD_X = 3;

/**
 * Given its points rather than an account id.
 *
 * A loan draws exactly this chart — what the borrowed money is worth against
 * what it put in — off a different walk, and the only thing that differs is what
 * the dashed line is called. Passing the series in is what lets the two share
 * the drawing rather than the second one being a copy of it with two words
 * changed.
 */
export const AccountChart: Component<{ points: { t: number; value: number; cost: number }[]; costLabel?: string; height?: string }> = (props) => {
    const points = createMemo(() => props.points);
    const costLabel = () => props.costLabel ?? 'Invested';

    const span = createMemo(() => {
        const all = points();
        if (all.length < 2) return null;
        const values = all.flatMap((p) => [p.value, p.cost]);
        const lo = Math.min(...values, 0);
        const hi = Math.max(...values);
        return { t0: all[0].t, tSpan: all[all.length - 1].t - all[0].t || 1, lo, vSpan: hi - lo || Math.abs(hi) || 1 };
    });

    const x = (t: number): number => PAD_X + ((t - span()!.t0) / span()!.tSpan) * (VB_W - PAD_X * 2);
    const y = (v: number): number => VB_H - PAD - ((v - span()!.lo) / span()!.vSpan) * (VB_H - PAD * 2);

    const valuePath = createMemo(() => points().map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.t).toFixed(3)} ${y(p.value).toFixed(3)}`).join(' '));

    // Stepped: horizontal to the day of the deposit, then vertical by it.
    const costPath = createMemo(() =>
        points()
            .map((p, i) => (i === 0 ? `M${x(p.t).toFixed(3)} ${y(p.cost).toFixed(3)}` : `L${x(p.t).toFixed(3)} ${y(points()[i - 1].cost).toFixed(3)} L${x(p.t).toFixed(3)} ${y(p.cost).toFixed(3)}`))
            .join(' '),
    );

    const [over, setOver] = createSignal(false);
    const [cursor, setCursor] = createSignal({ x: 0, y: 0 });

    const at = createMemo(() => {
        const all = points();
        if (all.length === 0) return null;
        const t = hoverAt();
        if (t == null) return all[all.length - 1];
        let found = all[0];
        for (const p of all) {
            if (p.t > t) break;
            found = p;
        }
        return found;
    });

    const track = (e: MouseEvent & { currentTarget: HTMLDivElement }) => {
        const rect = e.currentTarget.getBoundingClientRect();
        if (rect.width <= 0 || !span()) return;
        // Inverts x() — the plot is inset by PAD_X at each end, and taking the
        // fraction over the whole box slides the crosshair off the cursor.
        const across = ((e.clientX - rect.left) / rect.width) * VB_W;
        const fraction = Math.min(1, Math.max(0, (across - PAD_X) / (VB_W - PAD_X * 2)));
        setHoverAt(span()!.t0 + fraction * span()!.tSpan);
        setCursor({ x: e.clientX, y: e.clientY });
        setOver(true);
    };

    const gain = () => (at() == null ? null : at()!.value - at()!.cost);
    const gainPct = () => (at() == null || at()!.cost <= 0 ? null : ((at()!.value - at()!.cost) / at()!.cost) * 100);

    return (
        <Show
            when={span()}
            fallback={
                <div class='flex min-h-0 flex-1 items-center justify-center'>
                    <Text tone='muted'>{points().length === 0 ? 'No trades recorded yet' : 'Not enough history to chart'}</Text>
                </div>
            }>
            <div class={`relative w-full ${props.height ?? 'h-56'}`} onMouseMove={track} onMouseLeave={() => { setOver(false); clearHover(); }}>
                <svg class='h-full w-full' viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio='none' fill='none'>
                    <path d={costPath()} class='stroke-muted-foreground' stroke-width='1.5' stroke-dasharray='5 3' stroke-linecap='butt' vector-effect='non-scaling-stroke' />
                    <path d={valuePath()} class='stroke-foreground' stroke-width='2' stroke-linejoin='round' stroke-linecap='round' vector-effect='non-scaling-stroke' />
                    <Show when={hoverAt() != null}>
                        <line x1={x(hoverAt()!)} y1='0' x2={x(hoverAt()!)} y2={VB_H} class='stroke-muted-foreground' stroke-width='1' vector-effect='non-scaling-stroke' />
                    </Show>
                </svg>

                <Show when={!over()}>
                    <div class='pointer-events-none absolute top-0 left-0 flex items-center gap-3 px-2 py-1 whitespace-nowrap'>
                        <span class='flex items-center gap-1'>
                            <svg width='14' height='6' viewBox='0 0 14 6' class='shrink-0 text-foreground' aria-hidden='true'>
                                <line x1='0' y1='3' x2='14' y2='3' stroke='currentColor' stroke-width='2' />
                            </svg>
                            <Text tone='muted'>Value</Text>
                        </span>
                        <span class='flex items-center gap-1'>
                            <svg width='14' height='6' viewBox='0 0 14 6' class='shrink-0 text-muted-foreground' aria-hidden='true'>
                                <line x1='0' y1='3' x2='14' y2='3' stroke='currentColor' stroke-width='1.5' stroke-dasharray='5 3' />
                            </svg>
                            <Text tone='muted'>{costLabel()}</Text>
                        </span>
                    </div>
                </Show>
            </div>

            <Show when={over() && at()}>
                <Portal>
                    <div
                        class='pointer-events-none fixed z-[200] text-foreground rounded border bg-background px-2 py-1 shadow'
                        style={{ left: `${Math.min(cursor().x + 12, window.innerWidth - 240)}px`, top: `${Math.min(cursor().y + 12, window.innerHeight - 96)}px` }}>
                        <Text tone='muted' class='block whitespace-nowrap'>
                            {formatDay(new Date(at()!.t).toISOString().slice(0, 10))}
                        </Text>
                        <div class='flex items-center justify-between gap-3 whitespace-nowrap'>
                            <Text tone='muted'>Value</Text>
                            <Text weight='medium'>{formatCurrency(at()!.value)}</Text>
                        </div>
                        <div class='flex items-center justify-between gap-3 whitespace-nowrap'>
                            <Text tone='muted'>{costLabel()}</Text>
                            <Text>{formatCurrency(at()!.cost)}</Text>
                        </div>
                        <div class='flex items-center justify-between gap-3 whitespace-nowrap'>
                            <Text tone='muted'>Gain</Text>
                            <Text tone={gain() == null ? undefined : gain()! >= 0 ? 'green' : 'red'}>
                                {formatCurrency(gain())} · {formatPercent(gainPct(), 1)}
                            </Text>
                        </div>
                    </div>
                </Portal>
            </Show>
        </Show>
    );
};

export default AccountChart;
